// src/hooks/useProductOperations.js
import { useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

export const useProductOperations = () => {
  const createProduct = useCallback(async (formData) => {
    // Insert product record with pricing and free shipping fields
    const { data: prod, error: prodErr } = await supabase
      .from('products')
      .insert([{
        name_ar: formData.name_ar,
        description_ar: formData.description_ar,
        category_id: formData.category_id,
        price: parseFloat(formData.price) || 0,
        new_price: formData.new_price ? parseFloat(formData.new_price) : null,
        free_shipping: formData.free_shipping || false
      }])
      .select()
      .single();
    if (prodErr) throw prodErr;

    // Insert color records with color_order and is_home_color
    const cols = formData.colors.map(c => ({ 
      product_id: prod.id, 
      color_id: c.color_id,
      color_order: c.color_order || 0,
      is_home_color: c.is_home_color || false
    }));
    
    const { data: insertedCols, error: colsErr } = await supabase
      .from('product_colors')
      .insert(cols)
      .select();
    if (colsErr) throw colsErr;

    // Insert size records (without price fields)
    const sizeInserts = [];
    insertedCols.forEach((col, idx) => {
      (formData.colors[idx].sizes || []).forEach(s => {
        sizeInserts.push({
          product_color_id: col.id,
          size: s.size,
          stock: parseInt(s.stock, 10) || 0
        });
      });
    });
    if (sizeInserts.length) {
      const { error: szErr } = await supabase.from('product_color_sizes').insert(sizeInserts);
      if (szErr) throw szErr;
    }

    // Insert image records
    const imgInserts = [];
    insertedCols.forEach((col, idx) => {
      (formData.colors[idx].images || []).forEach((url, imgIdx) => {
        imgInserts.push({
          product_color_id: col.id,
          image_url: url,
          image_order: imgIdx
        });
      });
    });
    if (imgInserts.length) {
      const { error: imgErr } = await supabase.from('product_color_images').insert(imgInserts);
      if (imgErr) throw imgErr;
    }

    return prod;
  }, []);

  const updateProductColors = useCallback(async (formData, existingColors) => {
    const existingColorsMap = new Map(existingColors.map(c => [c.id, c]));
    const formColorsMap = new Map(formData.colors.filter(c => c.id).map(c => [c.id, c]));
    
    // Colors to delete
    const colorsToDelete = existingColors.filter(c => !formColorsMap.has(c.id)).map(c => c.id);
    
    // Colors to update
    const colorsToUpdate = [];
    for (const [id, formColor] of formColorsMap.entries()) {
      const existingColor = existingColorsMap.get(id);
      if (existingColor && (
        existingColor.color_id !== formColor.color_id ||
        existingColor.color_order !== formColor.color_order ||
        existingColor.is_home_color !== formColor.is_home_color
      )) {
        colorsToUpdate.push({ 
          id, 
          color_id: formColor.color_id,
          color_order: formColor.color_order || 0,
          is_home_color: formColor.is_home_color || false
        });
      }
    }
    
    // Colors to add
    const colorsToAdd = formData.colors
      .filter(c => !c.id)
      .map(c => ({ 
        product_id: formData.id, 
        color_id: c.color_id,
        color_order: c.color_order || 0,
        is_home_color: c.is_home_color || false
      }));
    
    // Process deletions
    if (colorsToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('product_colors')
        .delete()
        .in('id', colorsToDelete);
      if (deleteError) throw deleteError;
    }
    
    // Process updates
    for (const color of colorsToUpdate) {
      const { error: updateColorError } = await supabase
        .from('product_colors')
        .update({ 
          color_id: color.color_id,
          color_order: color.color_order,
          is_home_color: color.is_home_color
        })
        .eq('id', color.id);
      if (updateColorError) throw updateColorError;
    }
    
    // Process additions
    let newlyAddedColors = [];
    if (colorsToAdd.length > 0) {
      const { data: addedColors, error: addError } = await supabase
        .from('product_colors')
        .insert(colorsToAdd)
        .select();
      if (addError) throw addError;
      newlyAddedColors = addedColors;
    }
    
    // Update form colors with DB IDs
    const updatedFormColors = [...formData.colors];
    let newColorIndex = 0;
    for (let i = 0; i < updatedFormColors.length; i++) {
      if (!updatedFormColors[i].id && newlyAddedColors[newColorIndex]) {
        updatedFormColors[i].id = newlyAddedColors[newColorIndex].id;
        newColorIndex++;
      }
    }
    
    // Update sizes and images for each color
    for (const color of updatedFormColors) {
      if (!color.id) continue;
      await updateSizesForColor(color);
      await updateImagesForColor(color);
    }
  }, []);

  const updateProduct = useCallback(async (formData) => {
    // Update product record with pricing fields and free shipping
    const { error: updateError } = await supabase
      .from('products')
      .update({
        name_ar: formData.name_ar,
        description_ar: formData.description_ar,
        category_id: formData.category_id,
        price: parseFloat(formData.price) || 0,
        new_price: formData.new_price ? parseFloat(formData.new_price) : null,
        free_shipping: formData.free_shipping || false
      })
      .eq('id', formData.id);
      
    if (updateError) throw updateError;
    
    // Get current colors from database
    const { data: existingColors, error: colorsFetchError } = await supabase
      .from('product_colors')
      .select('id, color_id, color_order, is_home_color')
      .eq('product_id', formData.id);
    
    if (colorsFetchError) throw colorsFetchError;
    
    // Process color changes
    await updateProductColors(formData, existingColors);
  }, [updateProductColors]);

  const updateSizesForColor = async (color) => {
    const { data: existingSizes, error: sizesError } = await supabase
      .from('product_color_sizes')
      .select('id, size, stock')
      .eq('product_color_id', color.id);
      
    if (sizesError) throw sizesError;
    
    const existingSizesMap = new Map(existingSizes.map(s => [s.id, s]));
    const formSizesMap = new Map(
      (color.sizes || []).filter(s => s.id).map(s => [s.id, s])
    );
    
    // Sizes to delete
    const sizesToDelete = existingSizes
      .filter(s => !formSizesMap.has(s.id))
      .map(s => s.id);
    
    // Sizes to update
    const sizesToUpdate = [];
    for (const [id, formSize] of formSizesMap.entries()) {
      const existingSize = existingSizesMap.get(id);
      if (existingSize && (
        existingSize.size !== formSize.size || 
        parseInt(existingSize.stock) !== parseInt(formSize.stock)
      )) {
        sizesToUpdate.push({
          id,
          size: formSize.size,
          stock: parseInt(formSize.stock) || 0
        });
      }
    }
    
    // Sizes to add
    const sizesToAdd = (color.sizes || [])
      .filter(s => !s.id)
      .map(s => ({
        product_color_id: color.id,
        size: s.size,
        stock: parseInt(s.stock, 10) || 0
      }));
    
    // Execute operations
    if (sizesToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('product_color_sizes')
        .delete()
        .in('id', sizesToDelete);
      if (deleteError) throw deleteError;
    }
    
    for (const size of sizesToUpdate) {
      const { error: updateError } = await supabase
        .from('product_color_sizes')
        .update({ 
          size: size.size, 
          stock: size.stock
        })
        .eq('id', size.id);
      if (updateError) throw updateError;
    }
    
    if (sizesToAdd.length > 0) {
      const { error: addError } = await supabase
        .from('product_color_sizes')
        .insert(sizesToAdd);
      if (addError) throw addError;
    }
  };

  const updateImagesForColor = async (color) => {
    // Delete all existing images for this color
    const { error: deleteError } = await supabase
      .from('product_color_images')
      .delete()
      .eq('product_color_id', color.id);
    if (deleteError) throw deleteError;
    
    // Re-insert images with current order
    const imagesToAdd = (color.images || []).map((url, index) => ({
      product_color_id: color.id,
      image_url: url,
      image_order: index
    }));
    
    if (imagesToAdd.length > 0) {
      const { error: addError } = await supabase
        .from('product_color_images')
        .insert(imagesToAdd);
      if (addError) throw addError;
    }
  };

  const deleteProduct = useCallback(async (productId) => {
    // Delete product (this will cascade delete related records if FK constraints are set up properly)
    const { error: deleteError } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);
    
    if (deleteError) throw deleteError;
  }, []);

  const duplicateProduct = useCallback(async (originalProductId) => {
    // First, get the original product data
    const { data: originalProduct, error: productError } = await supabase
      .from('products')
      .select('*')
      .eq('id', originalProductId)
      .single();
    
    if (productError) throw productError;
    
    // Get product colors with order and home color info
    const { data: originalColors, error: colorsError } = await supabase
      .from('product_colors')
      .select('*')
      .eq('product_id', originalProductId)
      .order('color_order', { ascending: true });
    
    if (colorsError) throw colorsError;
    
    // Create new product with modified name, pricing, and free shipping
    const { data: newProduct, error: newProductError } = await supabase
      .from('products')
      .insert([{
        name_ar: `${originalProduct.name_ar} - نسخة`,
        description_ar: originalProduct.description_ar,
        category_id: originalProduct.category_id,
        price: originalProduct.price,
        new_price: originalProduct.new_price,
        free_shipping: originalProduct.free_shipping
      }])
      .select()
      .single();
    
    if (newProductError) throw newProductError;
    
    // Duplicate colors and their associated data
    for (const originalColor of originalColors) {
      // Insert new color with order and home color info
      const { data: newColor, error: newColorError } = await supabase
        .from('product_colors')
        .insert([{
          product_id: newProduct.id,
          color_id: originalColor.color_id,
          color_order: originalColor.color_order,
          is_home_color: originalColor.is_home_color
        }])
        .select()
        .single();
      
      if (newColorError) throw newColorError;
      
      // Get original sizes (without price fields)
      const { data: originalSizes, error: sizesError } = await supabase
        .from('product_color_sizes')
        .select('size, stock')
        .eq('product_color_id', originalColor.id);
      
      if (sizesError) throw sizesError;
      
      // Insert new sizes (without price fields)
      if (originalSizes.length > 0) {
        const newSizes = originalSizes.map(size => ({
          product_color_id: newColor.id,
          size: size.size,
          stock: size.stock
        }));
        
        const { error: newSizesError } = await supabase
          .from('product_color_sizes')
          .insert(newSizes);
        
        if (newSizesError) throw newSizesError;
      }
      
      // Get original images
      const { data: originalImages, error: imagesError } = await supabase
        .from('product_color_images')
        .select('*')
        .eq('product_color_id', originalColor.id)
        .order('image_order');
      
      if (imagesError) throw imagesError;
      
      // Insert new images
      if (originalImages.length > 0) {
        const newImages = originalImages.map(image => ({
          product_color_id: newColor.id,
          image_url: image.image_url,
          image_order: image.image_order
        }));
        
        const { error: newImagesError } = await supabase
          .from('product_color_images')
          .insert(newImages);
        
        if (newImagesError) throw newImagesError;
      }
    }
    
    return newProduct;
  }, []);

  const reorderProductColors = useCallback(async (productId, colorOrders) => {
    // colorOrders is an array of { id, color_order } objects
    try {
      for (const colorOrder of colorOrders) {
        const { error } = await supabase
          .from('product_colors')
          .update({ color_order: colorOrder.color_order })
          .eq('id', colorOrder.id);
        
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error reordering colors:', error);
      throw error;
    }
  }, []);

  const setHomeColor = useCallback(async (productId, colorId) => {
    try {
      // First, remove home color from all colors of this product
      const { error: resetError } = await supabase
        .from('product_colors')
        .update({ is_home_color: false })
        .eq('product_id', productId);
      
      if (resetError) throw resetError;
      
      // Then set the specified color as home color
      const { error: setError } = await supabase
        .from('product_colors')
        .update({ is_home_color: true })
        .eq('id', colorId);
      
      if (setError) throw setError;
    } catch (error) {
      console.error('Error setting home color:', error);
      throw error;
    }
  }, []);

  return {
    createProduct,
    updateProduct,
    deleteProduct,
    duplicateProduct,
    reorderProductColors,
    setHomeColor
  };
};
